<script>
  import ContextMenu from "./ContextMenu.svelte";
  import { showToast } from "./stores.js";
  import {
    exportAsCSV,
    exportAsTXT,
    exportAsPDF,
    exportAsJSON,
    copyJSONToClipboard,
    copyAsTXT
  } from "./exportUtils.js";

  // The whole calculation object is passed in. `bind:calculation` creates a two-way binding.
  export let calculation;

  let contextMenu = { show: false, x: 0, y: 0, items: [] };
  let selectedRowIndices = new Set();
  let lastClickedIndex = -1;
  let isMouseDown = false;

  // Watermark configuration - easily editable
  const WATERMARK_CONFIG = {
    text: "Created with Salary Breakdown Pro",
    website: "https://www.appsphinx.com/developer/shree/salary-breakdown-pro ",
    separator: " | Visit "
  };

  let tableMenu = { show: false, x: 0, y: 0 };
  $: showColumns = {
    basic: true,
    daRate: true,
    daAmount: true,
    due: true,
    drawn: true,
    commuted: calculation.salaryType === 'pensioner' && calculation.commutation?.opted,
    arrears: true,
    difference: true
  };

  // --- Reactive Recalculations ---
  // This reactive block re-calculates derived values for every row
  // AND the grand totals whenever `calculation.tableData` changes.
  $: totals = calculation.tableData.reduce(
    (acc, row) => {
      // Ensure all values are parsed as numbers, defaulting to 0.
      const basicPay = parseFloat(row.basicPay) || 0;
      const daRate = parseFloat(row.daRate) || 0;
      const drawn = parseFloat(row.drawn) || 0;
      const arrears = parseFloat(row.arrears) || 0;
      const commuted = (calculation.commutation?.opted && parseFloat(row.commuted)) || 0;

      // Perform calculations
      const daAmount = Math.round(basicPay * (daRate / 100));
      const salaryDue = basicPay + daAmount;
      // The "Difference" is Due - Drawn (irrespective of Arrears/Commuted)
      const difference = salaryDue - drawn;

      // Update the row object with calculated values for display
      row.daAmount = daAmount;
      row.salaryDue = salaryDue;
      row.difference = difference;

      // Aggregate totals
      acc.due += salaryDue;
      acc.drawn += drawn;
      acc.arrears += arrears;
      acc.commuted += commuted;
      acc.difference += difference;
      return acc;
    },
    { due: 0, drawn: 0, arrears: 0, commuted: 0, difference: 0 }
  );

  // Calculate amount still to be received (ignores commuted)
  $: dueAmount = totals.due - totals.drawn - totals.arrears;
  $: shouldHighlightDue = dueAmount > 100; // Only highlight if due amount is more than ₹100

  // Helper to recalculate derived row fields
  function recalcRow(row) {
    const daRate = parseFloat(row.daRate) || 0;
    row.daAmount = Math.round(row.basicPay * (daRate / 100));
    row.salaryDue = row.basicPay + row.daAmount;
    row.difference = row.salaryDue - (parseFloat(row.drawn) || 0);
  }

  // --- Smart Manual Override for DA% (FR-2.3) ---
  function handleDaRateInput(event, index) {
    try {
      let value = event.target.value;
      value = value.replace(/[^0-9.]/g, "");

      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      const numValue = parseFloat(value);
      if (numValue > 200) {
        value = "200";
      }

      calculation.tableData[index].daRate = value;
      recalcRow(calculation.tableData[index]);
      calculation.tableData = [...calculation.tableData]; // Trigger reactivity

      if (calculation.daSource !== "manual") {
        calculation.daSource = "manual";
        showToast("DA Source switched to 'Manual Entry' due to direct editing.", "info");
      }
    } catch (error) {
      console.error("Error handling DA rate input:", error);
      showToast("Error updating DA rate", "error");
    }
  }

  function evaluateExpression(expr) {
    // Remove all spaces
    expr = expr.replace(/\s+/g, '');
    
    // If no operators, just return the number
    if (!/[+\-*/%]/.test(expr)) {
      return parseFloat(expr);
    }

    try {
      // Split by operator while keeping the operator
      const parts = expr.split(/([+\-*/%])/);
      if (parts.length !== 3) return parseFloat(expr);

      const num1 = parseFloat(parts[0]);
      const operator = parts[1];
      const num2 = parseFloat(parts[2]);

      if (isNaN(num1) || isNaN(num2)) return 0;

      switch (operator) {
        case '+': return num1 + num2;
        case '-': return num1 - num2;
        case '*': return num1 * num2;
        case '/': return num2 === 0 ? 0 : num1 / num2;
        case '%': return num2 === 0 ? 0 : num1 % num2;
        default: return parseFloat(expr);
      }
    } catch (error) {
      console.error("Error evaluating expression:", error);
      return 0;
    }
  }

  function handleArrearsInput(event, index) {
    let value = event.target.value;
    
    // Allow numbers, decimal point, and basic arithmetic operators
    if (!/^[\d.+\-*/%]*$/.test(value)) {
      event.target.value = value.replace(/[^\d.+\-*/%]/g, '');
      return;
    }

    // Store the raw input value in the row for now
    calculation.tableData[index].arrearsInput = value;
  }

  function handleArrearsComplete(event, index) {
    const value = event.target.value;
    let numericValue;

    try {
      numericValue = evaluateExpression(value);
    } catch (error) {
      console.error("Error evaluating expression:", error);
      numericValue = 0;
    }

    if (isNaN(numericValue)) numericValue = 0;
    
    // Update both the display value and the stored value
    event.target.value = numericValue;
    calculation.tableData[index].arrears = numericValue;
    calculation.tableData[index].arrearsInput = String(numericValue);
    
    recalcRow(calculation.tableData[index]);
    calculation.tableData = [...calculation.tableData];
  }

  function handleNumberInput(event, index, field) {
    if (field === 'arrears') return; // Arrears are handled separately
    
    let value = parseFloat(event.target.value);
    if (isNaN(value)) value = 0;
    calculation.tableData[index][field] = value;
    recalcRow(calculation.tableData[index]);
    calculation.tableData = [...calculation.tableData];
  }

  function handleKeyDown(event, index) {
    if (event.key === 'Enter') {
      handleArrearsComplete(event, index);
    }
  }

  // --- Export Menu Actions ---
  function handleExport(action, pdfOrientation = 'landscape') {
    switch (action) {
      case 'csv':
        exportAsCSV(calculation, totals, dueAmount, shouldHighlightDue);
        break;
      case 'txt':
        exportAsTXT(calculation, totals, dueAmount, shouldHighlightDue);
        break;
      case 'pdf-landscape':
        exportAsPDF(calculation, totals, dueAmount, shouldHighlightDue, 'landscape');
        break;
      case 'pdf-portrait':
        exportAsPDF(calculation, totals, dueAmount, shouldHighlightDue, 'portrait');
        break;
      case 'copy-txt':
        copyAsTXT(calculation, totals, dueAmount, shouldHighlightDue);
        break;
      case 'json':
        exportAsJSON(calculation);
        break;
      case 'copy-json':
        copyJSONToClipboard(calculation);
        break;
      default:
        showToast('Unknown export action', 'error');
    }
  }

  // --- Selection Logic ---
  function handleRowMouseDown(event, index) {
    isMouseDown = true;
    if (event.shiftKey && lastClickedIndex > -1) {
      // Select a range of rows
      selectedRowIndices.clear();
      const start = Math.min(index, lastClickedIndex);
      const end = Math.max(index, lastClickedIndex);
      for (let i = start; i <= end; i++) selectedRowIndices.add(i);
    } else if (event.ctrlKey || event.metaKey) {
      // Toggle a single row in the selection
      selectedRowIndices.has(index)
        ? selectedRowIndices.delete(index)
        : selectedRowIndices.add(index);
    } else {
      // Standard click. If clicking a non-selected row, clear others.
      if (!selectedRowIndices.has(index)) {
        selectedRowIndices.clear();
        selectedRowIndices.add(index);
      }
    }
    lastClickedIndex = index;
    selectedRowIndices = selectedRowIndices; // Force Svelte to update the view
  }

  function handleRowMouseOver(index) {
    if (isMouseDown) {
      // Drag-to-select logic
      const start = Math.min(index, lastClickedIndex);
      const end = Math.max(index, lastClickedIndex);
      const newSelection = new Set();
      for (let i = start; i <= end; i++) newSelection.add(i);
      selectedRowIndices = newSelection;
    }
  }

  function handleRowFocus(index) {
    // Handle keyboard navigation for focus events
    if (isMouseDown) {
      const start = Math.min(index, lastClickedIndex);
      const end = Math.max(index, lastClickedIndex);
      const newSelection = new Set();
      for (let i = start; i <= end; i++) newSelection.add(i);
      selectedRowIndices = newSelection;
    }
  }

  // --- Context Menu Logic ---
  function handleRightClick(event, index) {
    event.preventDefault();
    // If right-clicking a row not in the current selection, make it the only selection
    if (!selectedRowIndices.has(index)) {
      selectedRowIndices = new Set([index]);
      lastClickedIndex = index;
    }
    const label =
      selectedRowIndices.size > 1
        ? `Auto-fill Due for ${selectedRowIndices.size} Selected`
        : "Auto-fill Due for this Month";
    contextMenu = {
      show: true,
      x: event.clientX,
      y: event.clientY,
      items: [{ label, action: "autofill" }],
    };
  }

  function handleContextMenuAction(event) {
    if (event.detail === "autofill") {
      selectedRowIndices.forEach((index) => {
        const row = calculation.tableData[index];
        if (row) {
          row.drawn = row.salaryDue;
        }
      });
      // Re-assigning the array triggers Svelte's reactivity
      calculation.tableData = [...calculation.tableData];
    }
  }

  function openTableMenu(event) {
    event.preventDefault();
    tableMenu = { show: true, x: event.clientX, y: event.clientY };
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  function handleTableMenuAction(action) {
    if (action === 'reset-table') {
      showColumns = {
        basic: true,
        daRate: true,
        daAmount: true,
        due: true,
        drawn: true,
        commuted: calculation.salaryType === 'pensioner' && calculation.commutation?.opted,
        arrears: true,
        difference: true
      };
      showToast('Table columns reset to default.', 'info');
    }
  }
</script>

<svelte:window
  on:mouseup={() => (isMouseDown = false)}
  on:click={() => {
    contextMenu.show = false;
    tableMenu.show = false;
  }}
/>

{#if contextMenu.show}
  <ContextMenu
    {...contextMenu}
    on:close={() => (contextMenu.show = false)}
    on:action={handleContextMenuAction}
  />
{/if}

{#if tableMenu.show}
  <ContextMenu
    x={tableMenu.x}
    y={tableMenu.y}
    items={[
      { label: showColumns.basic ? 'Hide Basic' : 'Show Basic', action: 'toggle-basic' },
      { label: showColumns.daRate ? 'Hide DA %' : 'Show DA %', action: 'toggle-daRate' },
      { label: showColumns.daAmount ? 'Hide DA Amount' : 'Show DA Amount', action: 'toggle-daAmount' },
      { label: showColumns.due ? 'Hide Due' : 'Show Due', action: 'toggle-due' },
      { label: showColumns.drawn ? 'Hide Drawn' : 'Show Drawn', action: 'toggle-drawn' },
      { label: showColumns.arrears ? 'Hide Arrears' : 'Show Arrears', action: 'toggle-arrears' },
      { label: showColumns.difference ? 'Hide Difference' : 'Show Difference', action: 'toggle-difference' },
      { isSeparator: true },
      { label: 'Reset Table Columns', action: 'reset-table' }
    ]}
    on:close={() => (tableMenu.show = false)}
    on:action={(e) => {
      const action = e.detail;
      if (action.startsWith('toggle-')) {
        const col = action.replace('toggle-', '');
        showColumns[col] = !showColumns[col];
      } else {
        handleTableMenuAction(action);
      }
    }}
  />
{/if}

<div class="glass-card">
  <div class="results-header" style="display: flex; align-items: center; justify-content: space-between;">
    <h2>Calculation Results</h2>
    <button class="table-menu-btn" title="Table Options" on:click|stopPropagation={openTableMenu}>⋯</button>
  </div>

  <div class="table-wrapper">
    <table id="salaryTable">
      <thead>
        <tr>
          <th>Month/Year</th>
          {#if showColumns.basic}<th>Basic (₹)</th>{/if}
          {#if showColumns.daRate}<th>DA %</th>{/if}
          {#if showColumns.daAmount}<th>DA (₹)</th>{/if}
          {#if showColumns.due}<th>Due (₹)</th>{/if}
          {#if showColumns.drawn}<th>Drawn (₹)</th>{/if}
          {#if showColumns.commuted && calculation.salaryType === "pensioner" && calculation.commutation?.opted}
            <th>Commuted (₹)</th>
          {/if}
          {#if showColumns.arrears}<th>Arrears (₹)</th>{/if}
          {#if showColumns.difference}<th>Difference (₹)</th>{/if}
        </tr>
      </thead>
      <!-- The contextmenu event is on the tbody to catch all right clicks within it -->
      <tbody on:contextmenu|preventDefault={(e) => e.preventDefault()}>
        {#each calculation.tableData as row, i (row.monthYear)}
          <!-- All interaction events are on the `tr` element -->
          <tr
            class:selected={selectedRowIndices.has(i)}
            on:mousedown={(e) => handleRowMouseDown(e, i)}
            on:mouseover={() => handleRowMouseOver(i)}
            on:focus={() => handleRowFocus(i)}
            on:contextmenu={(e) => handleRightClick(e, i)}
          >
            <td>{row.monthYear}</td>
            {#if showColumns.basic}<td>{row.basicPay.toFixed(2)}</td>{/if}
            {#if showColumns.daRate}
              <td>
                <div class="cell-input-wrapper">
                  <!-- Use on:input for live updates and bind:value for reactivity -->
                  <input
                    type="text"
                    inputmode="decimal"
                    class="cell-input"
                    bind:value={row.daRate}
                    on:input={(e) => handleDaRateInput(e, i)}
                  />
                  <span>%</span>
                </div>
              </td>
            {/if}
            {#if showColumns.daAmount}<td>{(row.daAmount || 0).toFixed(2)}</td>{/if}
            {#if showColumns.due}<td>{(row.salaryDue || 0).toFixed(2)}</td>{/if}
            {#if showColumns.drawn}
              <td>
                <input
                  type="number"
                  class="cell-input"
                  bind:value={row.drawn}
                  min="0"
                  step="0.01"
                  on:input={(e) => handleNumberInput(e, i, 'drawn')}
                />
              </td>
            {/if}
            {#if showColumns.commuted && calculation.salaryType === "pensioner" && calculation.commutation?.opted}
              <td>{(row.commuted || 0).toFixed(2)}</td>
            {/if}
            {#if showColumns.arrears}
              <td class="number-cell">
                <div class="cell-input-wrapper">
                  <input
                    type="text"
                    class="cell-input"
                    value={row.arrearsInput || row.arrears || 0}
                    on:input={(e) => handleArrearsInput(e, i)}
                    on:blur={(e) => handleArrearsComplete(e, i)}
                    on:keydown={(e) => handleKeyDown(e, i)}
                    title="Enter a value or use basic arithmetic (e.g., 1000*4, 2000+500, 1500-300, 3000/2, 100%3). Press Enter or click outside to calculate."
                  />
                </div>
              </td>
            {/if}
            {#if showColumns.difference}<td>{row.difference.toFixed(2)}</td>{/if}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <!-- Summary Section -->
  <div id="summarySection">
    <div class="summary-section">
      <h4>Summary</h4>
      <div class="summary-grid">
        <div class="summary-item">
          <span class="label">Total Due</span>
          <span class="value">{formatCurrency(totals.due)}</span>
        </div>
        <div class="summary-item">
          <span class="label">Total Drawn <span class="sub-text">(Drawn + Arrears)</span></span>
          <span class="value">{formatCurrency(totals.drawn + totals.arrears)}</span>
        </div>
        <div class="summary-item">
          <span class="label">Total Arrears</span>
          <span class="value">{formatCurrency(totals.arrears)}</span>
        </div>
        {#if totals.commuted > 0}
          <div class="summary-item">
            <span class="label">Commuted Pension</span>
            <span class="value">{formatCurrency(totals.commuted)}</span>
          </div>
        {/if}
        <div class="summary-item">
          <span class="label">Total Difference</span>
          <span class="value">{formatCurrency(totals.difference)}</span>
        </div>
      </div>
    </div>

    <!-- Due Amount Highlighting Section -->
    {#if shouldHighlightDue}
      <div class="due-highlight-section">
        <div class="due-alert">
          <div class="due-icon">⚠️</div>
          <div class="due-content">
            <div class="due-label">Amount to be Received</div>
            <div class="due-amount">₹{dueAmount.toFixed(2)}</div>
          </div>
        </div>
      </div>
    {/if}

    <!-- Export Section -->
    <div class="export-section">
      <h4>Export Calculation</h4>
      <div class="export-buttons">
        <button class="export-btn" on:click={() => handleExport('csv')}>
          📊 Export CSV
        </button>
        <button class="export-btn" on:click={() => handleExport('txt')}>
          📄 Export TXT
        </button>
        <button class="export-btn" on:click={() => handleExport('pdf-landscape')}>
          📄 Export PDF (Landscape)
        </button>
        <button class="export-btn" on:click={() => handleExport('pdf-portrait')}>
          📄 Export PDF (Portrait)
        </button>
        <button class="export-btn" on:click={() => handleExport('copy-txt')}>
          📋 Copy as TXT
        </button>
        <button class="export-btn" on:click={() => handleExport('json')}>
          📄 Export JSON
        </button>
        <button class="export-btn" on:click={() => handleExport('copy-json')}>
          📋 Copy JSON to Clipboard
        </button>
      </div>
    </div>
  </div>
</div>

<style>
  .cell-input-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .cell-input {
    width: 80px;
    background: transparent;
    border: none;
    color: var(--text-color);
    text-align: right;
    border-radius: 4px;
    padding: 4px;
    font-family: inherit;
    font-size: inherit;
  }
  .cell-input:focus {
    outline: 1px solid var(--primary-color);
    background: rgba(0, 0, 0, 0.3);
  }
  td {
    padding: 4px 6px;
    vertical-align: middle;
  }
  tr.selected {
    /* Applied directly to the tr now */
    background-color: var(--highlight-bg) !important;
  }

  /* Due Amount Highlighting */
  .due-highlight-section {
    margin-top: 20px;
    padding: 20px;
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05));
    border: 2px solid #ef4444;
    border-radius: 12px;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }

  .due-alert {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .due-icon {
    font-size: 2rem;
  }

  .due-content {
    flex: 1;
  }

  .due-label {
    font-size: 0.9rem;
    color: var(--text-muted);
    margin-bottom: 5px;
  }

  .due-amount {
    font-size: 1.5rem;
    font-weight: bold;
    color: #ef4444;
  }

  /* Export Section */
  .export-section {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid var(--glass-border);
  }

  .export-section h4 {
    margin: 0 0 15px 0;
    color: var(--text-color);
    font-size: 1.1rem;
  }

  .export-buttons {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }

  .export-btn {
    background: var(--primary-color);
    color: white;
    border: none;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .export-btn:hover {
    background: var(--primary-color-dark, #0056b3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .export-btn:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    .export-buttons {
      grid-template-columns: 1fr;
    }
    
    .due-alert {
      flex-direction: column;
      text-align: center;
      gap: 10px;
    }
    
    .due-amount {
      font-size: 1.3rem;
    }
  }

  .table-menu-btn {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    color: var(--text-muted);
    font-size: 1.2rem;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    transition: all 0.2s ease;
    margin-left: 10px;
  }
  .table-menu-btn:hover {
    background: rgba(255,255,255,0.1);
    color: var(--text-color);
    border-color: var(--primary-color);
    transform: scale(1.05);
  }
  .table-menu-btn:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .sub-text {
    font-size: 0.8em;
    color: var(--text-muted);
    font-weight: normal;
  }
</style>
